// components/HomeFormModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  Chip,
  Stack,
  Card,
  CardContent,
  Grid,
  Switch,
  CircularProgress
} from '@mui/material';
import { Close, Delete, Add } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const HomeFormModal = ({ open, onClose, onSubmit, initialData, mode, loading  }) => {
  const [formData, setFormData] = useState({
    section_name: '',
    title: '',
    description: '',
    alignment: 'center',
    width_percentage: 100,
    content_order: ['title', 'description', 'image'],
    sort_order: 0,
    is_active: true,
  });
  const [image, setImage] = useState(null);
  const [video, setVideo] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link', 'image'
  ];

  useEffect(() => {
    if (initialData) {
      setFormData({
        section_name: initialData.section_name || '',
        title: initialData.title || '',
        description: initialData.description || '',
        alignment: initialData.alignment || 'center',
        width_percentage: initialData.width_percentage || 100,
        content_order: initialData.content_order || ['title', 'description', 'image'],
        sort_order: initialData.sort_order || 0,
        is_active: initialData.is_active ?? true,
      });
      
      if (initialData.image) {
        setImagePreview(`/${initialData.image}`);
      }
    } else {
      setFormData({
        section_name: '',
        title: '',
        description: '',
        alignment: 'center',
        width_percentage: 100,
        content_order: ['title', 'description', 'image'],
        sort_order: 0,
        is_active: true,
      });
      setImagePreview(null);
    }
    setImage(null);
    setVideo(null);
  }, [initialData, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDescriptionChange = (value) => {
    setFormData(prev => ({ ...prev, description: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoChange = (e) => {
    setVideo(e.target.files[0]);
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image: null }));
  };

  const addContentItem = (item) => {
    if (!formData.content_order.includes(item)) {
      setFormData(prev => ({
        ...prev,
        content_order: [...prev.content_order, item],
      }));
    }
  };

  const removeContentItem = (index) => {
    setFormData(prev => ({
      ...prev,
      content_order: prev.content_order.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData();
    
    // اضافه کردن فیلدها به FormData
    data.append('section_name', formData.section_name);
    data.append('title', formData.title);
    data.append('description', formData.description);
    data.append('alignment', formData.alignment);
    data.append('width_percentage', formData.width_percentage.toString());
    data.append('sort_order', formData.sort_order.toString());
    
    // تبدیل آرایه به JSON string برای content_order
    data.append('content_order', JSON.stringify(formData.content_order));
    
    // تبدیل boolean به string برای is_active
    data.append('is_active', formData.is_active.toString());
    
    if (image) {
      data.append('image', image);
    }
    if (video) {
      data.append('video', video);
    }

    

    onSubmit(data);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '95%',
        maxWidth: 900,
        maxHeight: '95vh',
        overflow: 'auto',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
      }}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={2}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              {mode === 'add' ? 'افزودن بخش جدید' : 'ویرایش بخش'}
            </MDTypography>
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </MDBox>

          <CardContent>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* اطلاعات اصلی */}
                <Grid item xs={12} md={6}>
                  <TextField
                    label="نام بخش"
                    name="section_name"
                    value={formData.section_name}
                    onChange={handleChange}
                    required
                    fullWidth
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="عنوان"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>تراز</InputLabel>
                    <Select
                      name="alignment"
                      value={formData.alignment}
                      onChange={handleChange}
                      label="تراز"
                    >
                      <MenuItem value="right">راست</MenuItem>
                      <MenuItem value="left">چپ</MenuItem>
                      <MenuItem value="center">وسط</MenuItem>
                      <MenuItem value="full">تمام صفحه</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="عرض (درصد)"
                    name="width_percentage"
                    type="number"
                    inputProps={{ min: 1, max: 100 }}
                    value={formData.width_percentage}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="اولویت نمایش"
                    name="sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          is_active: e.target.checked
                        }))}
                        color="primary"
                      />
                    }
                    label="فعال"
                    sx={{ mt: 2 }}
                  />
                </Grid>

                {/* توضیحات با ویرایشگر ریچ تکست */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    توضیحات
                  </Typography>
                  <ReactQuill
                    value={formData.description}
                    onChange={handleDescriptionChange}
                    modules={modules}
                    formats={formats}
                    theme="snow"
                    style={{ height: '200px', marginBottom: '50px' }}
                  />
                </Grid>

                {/* تصویر */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    تصویر
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ marginBottom: '10px' }}
                  />
                  {imagePreview && (
                    <Box mt={2} position="relative" display="inline-block">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        style={{ 
                          maxWidth: '200px', 
                          maxHeight: '150px',
                          borderRadius: '8px'
                        }} 
                      />
                      <IconButton
                        size="small"
                        onClick={removeImage}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          backgroundColor: 'error.main',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'error.dark',
                          }
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  )}
                </Grid>

                {/* ویدیو */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    ویدیو
                  </Typography>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoChange}
                  />
                  {initialData?.video && !video && (
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      ویدیوی فعلی: {initialData.video}
                    </Typography>
                  )}
                </Grid>

                {/* ترتیب محتوا */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    ترتیب نمایش محتوا
                  </Typography>
                  <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
                    {['title', 'description', 'image', 'video'].map(item => (
                      <Button
                        key={item}
                        variant="outlined"
                        size="small"
                        onClick={() => addContentItem(item)}
                        disabled={formData.content_order.includes(item)}
                        startIcon={<Add />}
                      >
                        {item === 'title' && 'عنوان'}
                        {item === 'description' && 'توضیحات'}
                        {item === 'image' && 'تصویر'}
                        {item === 'video' && 'ویدیو'}
                      </Button>
                    ))}
                  </Stack>
                  
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    ترتیب فعلی:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {formData.content_order.map((item, index) => (
                      <Chip
                        key={index}
                        label={
                          item === 'title' ? 'عنوان' :
                          item === 'description' ? 'توضیحات' :
                          item === 'image' ? 'تصویر' :
                          item === 'video' ? 'ویدیو' : item
                        }
                        onDelete={() => removeContentItem(index)}
                        color="primary"
                        variant="outlined"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                </Grid>

                {/* دکمه‌های action */}
                <Grid item xs={12}>
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="secondary"
                      onClick={onClose}
                      disabled={loading}
                    >
                      انصراف
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="info"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                            <CircularProgress size={20} color="inherit" /> 
                        ) : mode === 'add' ? (
                            'افزودن'
                        ) : (
                            'بروزرسانی'
                        )}
                    </MDButton>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Modal>
  );
};

export default HomeFormModal;